import { useMutation } from "@apollo/client";
import {
  Button,
  ButtonGroup,
  ControlGroup,
  InputGroup,
  Tag,
} from "@blueprintjs/core";
import { Select2 } from "@blueprintjs/select";
import { isEqual } from "lodash";
import { useEffect, useState } from "react";
import {
  ArticleParameter,
  UpdateArticleParameterInput,
} from "../../../../api/schema";
import { ValuesVariables } from "../../../../components/form/InputForm";
import { resolve_lang, UserLanguage } from "../../../../lang/lang";
import { AppToaster } from "../../../Template";
import {
  DeleteArticleParameterMutation,
  DeleteArticleParameterResponse,
  UpdateArticleParameterMutation,
} from "../../helper/graphql";
import { SuffixMenuItemRenderer } from "../../helper/SelectRenderer";
import { SimpleButton } from "../../ui/SimpleButton";

interface StringFieldProps {
  parameter: ArticleParameter;
  on_changed: () => void;
}

export const StringField = (props: StringFieldProps) => {
  const [delete_parameter_mutation] =
    useMutation<DeleteArticleParameterResponse>(DeleteArticleParameterMutation);

  const [update_parameter_mutation] = useMutation<
    ArticleParameter,
    ValuesVariables<UpdateArticleParameterInput>
  >(UpdateArticleParameterMutation);

  console.log("building string field", props);

  const field = props.parameter;
  const key_field = field.key_field;

  const [submitting, set_submitting] = useState(false);

  const [field_value, set_field_value] = useState(field.value);
  const [field_suffix, set_field_suffix] = useState(field.suffix);

  const [is_value_edited, set_value_edited] = useState(false);
  const [is_suffix_edited, set_suffix_edited] = useState(false);

  useEffect(() => {
    set_value_edited(!isEqual(field.value, field_value));
    set_suffix_edited(!isEqual(field.suffix, field_suffix));
  }, [field_value, field_suffix, field.value, field.suffix]);

  const save_parameter = () => {
    if (!(is_value_edited || is_suffix_edited)) return;

    set_submitting(true);

    const variables: ValuesVariables<UpdateArticleParameterInput> = {
      values: {
        id: field.id,
      },
    };

    if (is_value_edited) variables.values.value = field_value;
    if (is_suffix_edited) variables.values.suffix = field_suffix;

    update_parameter_mutation({ variables }).then((res) => {
      console.log("update_article_parameter", res);
      if (res.data) {
        AppToaster.show({
          message: UserLanguage().results.saved,
          intent: "success",
          icon: "tick",
        });
        set_submitting(false);
        props.on_changed();
      }
    });
  };

  const remove_parameter = () => {
    if (field.key_field.required) return;

    delete_parameter_mutation({ variables: { id: field.id } }).then((res) => {
      console.log("delete_article_parameter", res);
      if (res.data) {
        AppToaster.show({
          message: UserLanguage().results.deleted,
          intent: "success",
          icon: "tick",
        });
        props.on_changed();
      }
    });
  };

  let suffix_box = <></>;
  if (key_field.values.length > 0)
    suffix_box = (
      <Select2<string>
        disabled={submitting}
        items={key_field.values}
        itemRenderer={SuffixMenuItemRenderer}
        onItemSelect={(i) => set_field_suffix(i)}
        filterable={false}
      >
        <Button text={field_suffix} key={field.id} fill />
      </Select2>
    );

  return (
    <ControlGroup style={{ marginBottom: 10 }}>
      <Tag
        large
        intent="none"
        minimal
        style={{ flexShrink: 0, marginRight: 5 }}
      >
        {resolve_lang(field.key_field.name, UserLanguage().data.fields)}
      </Tag>
      <InputGroup
        type="text"
        fill
        value={field_value}
        onChange={(e) => set_field_value(e.currentTarget.value)}
        rightElement={
          is_value_edited ? (
            <Button
              icon="eraser"
              intent="warning"
              outlined
              onClick={() => set_field_value(field.value)}
            />
          ) : undefined
        }
      />
      {suffix_box}
      <ButtonGroup>
        <SimpleButton
          disabled={submitting}
          enabled={is_suffix_edited || is_value_edited}
          intent="success"
          onClick={save_parameter}
          icon="edit"
        />
        <SimpleButton
          disabled={submitting}
          enabled={!field.key_field.required}
          intent="danger"
          onClick={remove_parameter}
          icon="cross"
        />
      </ButtonGroup>
    </ControlGroup>
  );
};
